import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusBadge, TableChip, SkeletonText } from '@/components';
import { V2Table } from '@/common/components';
import {
  formatRemindAt,
  getReminderTypeLabel,
  getReminderTypeTone,
  getReminderStatusMeta,
  getRowActionPath,
} from '../utils';
import { ReminderRowActions } from './ReminderRowActions';

export function RemindersTable({
  reminders = [],
  isLoading,
  page = 1,
  totalCount = 0,
  pageSize = 10,
  onPageChange,
  onRowClick,
  onAction,
  canUpdate = true,
  actionsDisabled = false,
}) {
  const navigate = useNavigate();

  const handleRowClick = (reminder) => {
    if (onRowClick) {
      onRowClick(reminder);
      return;
    }
    const path = getRowActionPath(reminder);
    if (path) navigate(path, { state: { from: '/reminders' } });
  };

  const columns = useMemo(
    () => [
      {
        id: 'type',
        label: 'Type',
        defaultWidth: 160,
        minWidth: 130,
        skeleton: <SkeletonText width="w-24" height="h-5" />,
        cell: (reminder) => (
          <TableChip tone={getReminderTypeTone(reminder.type)}>
            {getReminderTypeLabel(reminder.type)}
          </TableChip>
        ),
      },
      {
        id: 'title',
        label: 'Title',
        defaultWidth: 260,
        minWidth: 180,
        cell: (reminder) => (
          <span
            className="truncate block font-medium"
            title={reminder.title || ''}
          >
            {reminder.title || '-'}
          </span>
        ),
      },
      {
        id: 'contact',
        label: 'Contact',
        defaultWidth: 160,
        minWidth: 120,
        cell: (reminder) => {
          const value =
            reminder.contact?.email || reminder.contact?.phone || '-';
          return (
            <span className="truncate block" title={value}>
              {value}
            </span>
          );
        },
      },
      {
        id: 'due',
        label: 'Due Date',
        defaultWidth: 180,
        minWidth: 140,
        cell: (reminder) => formatRemindAt(reminder.remind_at),
      },
      {
        id: 'status',
        label: 'Status',
        defaultWidth: 130,
        minWidth: 110,
        cell: (reminder) => {
          const statusMeta = getReminderStatusMeta(reminder.status);
          return (
            <StatusBadge tone={statusMeta.tone}>{statusMeta.label}</StatusBadge>
          );
        },
      },
      {
        id: 'assignee',
        label: 'Assignee',
        defaultWidth: 150,
        minWidth: 120,
        cell: (reminder) => (
          <span
            className="truncate block"
            title={reminder.assignee?.name || ''}
          >
            {reminder.assignee?.name || '-'}
          </span>
        ),
      },
      {
        id: 'actions',
        label: 'Actions',
        defaultWidth: 120,
        minWidth: 100,
        align: 'center',
        stopPropagation: true,
        cell: (reminder) => (
          <ReminderRowActions
            reminder={reminder}
            canUpdate={canUpdate}
            disabled={actionsDisabled}
            onAction={onAction}
          />
        ),
      },
    ],
    [actionsDisabled, canUpdate, onAction],
  );

  return (
    <div className="w-full min-w-0 flex-1 min-h-0 flex flex-col">
      <V2Table
        tight
        className="reminders-table"
        storageKey="reminders_table_column_widths"
        columns={columns}
        data={reminders}
        rowKey="id"
        loading={isLoading}
        loadingRowCount={pageSize || 10}
        onRowClick={handleRowClick}
        empty="No reminders found"
        pagination={{
          currentPage: page,
          totalCount,
          itemsPerPage: pageSize,
          onChange: onPageChange,
        }}
      />
    </div>
  );
}
